import { useEffect, useRef } from 'react';

/**
 * A custom React hook to dynamically manage SEO and social sharing meta tags.
 * Supports title, description, keywords, Open Graph, Twitter Cards, canonical links, and dynamic JSON-LD.
 *
 * @param {Object} seoConfig
 * @param {string} seoConfig.title - The page title
 * @param {string} seoConfig.description - The page meta description
 * @param {string} seoConfig.keywords - Page keywords comma-separated
 * @param {string} [seoConfig.ogType] - Open Graph type (e.g. 'website', 'article')
 * @param {string} [seoConfig.ogImage] - Open Graph image URL
 * @param {Object} [seoConfig.schema] - JSON-LD schema object
 */
export const useSEO = ({ title, description, keywords, ogType = 'website', ogImage, schema }) => {
  const schemaString = schema ? JSON.stringify(schema) : '';
  const prevSchemaString = useRef('');

  useEffect(() => {
    // Helper function to update or create a meta tag
    const updateOrCreateMetaTag = (attributeName, attributeValue, contentValue) => {
      if (!contentValue) return;
      let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', contentValue);
    };

    // Helper function to update or create link tag (e.g., canonical)
    const updateOrCreateLinkTag = (relValue, hrefValue) => {
      if (!hrefValue) return;
      let element = document.querySelector(`link[rel="${relValue}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', relValue);
        document.head.appendChild(element);
      }
      element.setAttribute('href', hrefValue);
    };

    // 1. Update Title
    const formattedTitle = title ? `${title} | Family Wishlist` : 'Family Wishlist - Bringing families closer, one wishlist at a time.';
    document.title = formattedTitle;

    // 2. Update Description
    updateOrCreateMetaTag('name', 'description', description);

    // 3. Update Keywords
    updateOrCreateMetaTag('name', 'keywords', keywords);

    // 4. Update Open Graph Tags
    const currentUrl = window.location.href;
    const resolvedOgImage = ogImage || `${window.location.origin}/fw_logo.png`;

    updateOrCreateMetaTag('property', 'og:title', formattedTitle);
    updateOrCreateMetaTag('property', 'og:description', description);
    updateOrCreateMetaTag('property', 'og:type', ogType);
    updateOrCreateMetaTag('property', 'og:url', currentUrl);
    updateOrCreateMetaTag('property', 'og:site_name', 'Family Wishlist');
    updateOrCreateMetaTag('property', 'og:image', resolvedOgImage);

    // 5. Update Twitter Card Meta Tags
    updateOrCreateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateOrCreateMetaTag('name', 'twitter:title', formattedTitle);
    updateOrCreateMetaTag('name', 'twitter:description', description);
    updateOrCreateMetaTag('name', 'twitter:image', resolvedOgImage);

    // 6. Update Canonical Link
    updateOrCreateLinkTag('canonical', currentUrl.split('?')[0]);

    // 7. Manage JSON-LD Schema
    let schemaScript = document.getElementById('json-ld-schema');
    if (schemaString) {
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'json-ld-schema';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = schemaString;
      prevSchemaString.current = schemaString;
    } else {
      if (schemaScript) {
        schemaScript.remove();
        prevSchemaString.current = '';
      }
    }

    // Optional cleanup on unmount
    return () => {
      const currentSchemaScript = document.getElementById('json-ld-schema');
      if (currentSchemaScript) {
        currentSchemaScript.remove();
      }
    };
  }, [title, description, keywords, ogType, ogImage, schemaString]);
};
